// Reads how many people live at a household from its Gegevens tab.
//
// WHY THIS IS TYPED IN RATHER THAN FETCHED. Two interventies in the
// milieu-circulariteit standaard are priced per bewoner — waterbesparende
// douchekop (ET07) and douchetimer (ET09) — and the standaard says plainly:
// "Leg het aantal bewoners per huishouden vast; anders is de besparing
// onzeker." No public source can supply it per address: BAG is a register of
// buildings, not of people; BRP has the real figure but needs a legal basis we
// do not have; and CBS publishes only a buurt-average, which applied to one
// household is the ecological fallacy ADR-030 exists to prevent. A one-person
// household in a buurt averaging 2,1 would have its saving overstated by more
// than 100%.
//
// MISSING IS NULL, NEVER 0. A 0 would make those interventies contribute
// nothing while looking like a measured result; null keeps them on status
// needs-residents so the UI can ask for the number.
//
// AI-CONTEXT: See docs/features/materials-co2-circulariteit.md

import sentinelToEmpty from '../sentinelValue'

/**
 * The Title a PersonaData row must carry. Offered by the datalist on the
 * Gegevens tab via STANDARDIZED_DATA_FIELDS, but the field is free text, so
 * matching is case- and whitespace-insensitive.
 */
const RESIDENT_COUNT_TITLE = 'Aantal bewoners'

/**
 * '' COUNTS AS A SENTINEL HERE — it already did: the old list left '' out but
 * the guard right below it read `if (!text || …)`, so a cleared field returned
 * null either way. Missing is null, never 0 (see the header).
 *
 * The shared list also carries 'none', which the old one did not. No behaviour
 * change: `Number('none')` is NaN, so it was rejected one line later anyway.
 * AI-CONTEXT: docs/pitfalls.md#database-url-sentinel-string-persisted
 *
 * @param {*} value - PersonaData.Value, always a string in practice
 * @returns {number|null} A whole number of people, or null when unusable
 */
const parseCount = (value) => {
    const text = sentinelToEmpty(value)
    if (!text) return null

    const parsed = Number(text)
    if (!Number.isFinite(parsed) || parsed <= 0) return null
    // People are whole; a stray '2.7' is rounded down rather than rejected.
    return Math.floor(parsed)
}

/**
 * @param {Array<{Title?: string, Value?: string}>} personaData - PersonaData rows for one adress
 * @returns {number|null}
 */
const readResidentCount = (personaData) => {
    if (!Array.isArray(personaData)) return null

    const target = RESIDENT_COUNT_TITLE.toLowerCase()
    for (const entry of personaData) {
        if (String(entry?.Title || '').trim().toLowerCase() !== target) continue
        const count = parseCount(entry?.Value)
        // Keep looking: the row can legitimately appear twice, and an empty
        // first one should not mask a filled second.
        if (count !== null) return count
    }
    return null
}

export default readResidentCount
export { RESIDENT_COUNT_TITLE }
